import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Loads GTM, GA4, Meta Pixel, and LinkedIn Insight tags when enabled (SK12 prod only).
 *
 * Scripts are injected at bootstrap via APP_INITIALIZER so index.html stays clean in dev.
 * Tag IDs come from environment.marketingTagIds (populated only in SK12 prod).
 */
@Injectable({ providedIn: 'root' })
export class MarketingTagsService {
  private readonly enabled = environment.features?.marketingTags ?? false;
  private readonly ids = environment.marketingTagIds;
  private loaded = false;

  load(): void {
    if (!this.enabled || this.loaded || typeof document === 'undefined' || !this.hasConfiguredIds()) {
      return;
    }

    this.loaded = true;
    if (this.ids.gtmId) {
      this.injectGtm();
    }
    if (this.ids.ga4Id) {
      this.injectGa4();
    }
    if (this.ids.metaPixelId) {
      this.injectMetaPixel();
    }
    if (this.ids.linkedInPartnerId) {
      this.injectLinkedIn();
    }
    this.injectNoscriptFallbacks();
  }

  private hasConfiguredIds(): boolean {
    const { gtmId, ga4Id, metaPixelId, linkedInPartnerId } = this.ids;
    return !!(gtmId || ga4Id || metaPixelId || linkedInPartnerId);
  }

  private injectGtm(): void {
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.ids.gtmId}`;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  }

  private injectGa4(): void {
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];

    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${this.ids.ga4Id}`;
    document.head.appendChild(loader);

    const config = document.createElement('script');
    config.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.ids.ga4Id}');
    `;
    document.head.appendChild(config);
  }

  private injectMetaPixel(): void {
    const script = document.createElement('script');
    script.text = `
      !function(f,b,e,v,n,t,s){
        if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)
      }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${this.ids.metaPixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }

  private injectLinkedIn(): void {
    const w = window as Window & {
      _linkedin_data_partner_ids?: string[];
      lintrk?: ((a: unknown, b?: unknown) => void) & { q?: unknown[][] };
    };

    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
    w._linkedin_data_partner_ids.push(this.ids.linkedInPartnerId);

    if (!w.lintrk) {
      w.lintrk = function (a: unknown, b?: unknown) {
        w.lintrk!.q = w.lintrk!.q || [];
        w.lintrk!.q!.push([a, b]);
      };
      w.lintrk.q = [];
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  }

  private injectNoscriptFallbacks(): void {
    const body = document.body;
    if (!body) return;

    const appRoot = document.querySelector('app-root');
    const noscripts: HTMLElement[] = [];

    if (this.ids.gtmId) {
      const gtmNoscript = document.createElement('noscript');
      gtmNoscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.ids.gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      noscripts.push(gtmNoscript);
    }

    if (this.ids.metaPixelId) {
      const metaNoscript = document.createElement('noscript');
      metaNoscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${this.ids.metaPixelId}&ev=PageView&noscript=1" />`;
      noscripts.push(metaNoscript);
    }

    if (this.ids.linkedInPartnerId) {
      const linkedInNoscript = document.createElement('noscript');
      linkedInNoscript.innerHTML = `<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${this.ids.linkedInPartnerId}&fmt=gif" />`;
      noscripts.push(linkedInNoscript);
    }

    for (const el of noscripts) {
      if (appRoot) {
        body.insertBefore(el, appRoot);
      } else {
        body.insertAdjacentElement('afterbegin', el);
      }
    }
  }
}
